import { Personnel, Character, CastAssignment } from '../types';
import { getCharacterIdsForPersonnel } from './cast-assignment-selectors';

export interface PersonnelProjectTitleOptions {
    characters?: Character[];
    castAssignments?: CastAssignment[];
}

const firstTrimmedValue = (...values: Array<string | null | undefined>): string => {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed) {
            return trimmed;
        }
    }

    return '';
};

export function getAssignedCharacterNames(
    member: Personnel,
    characters: Character[] = [],
    castAssignments: CastAssignment[] = [],
): string[] {
    if (member.category !== 'cast') {
        return [];
    }

    const characterIds = new Set(getCharacterIdsForPersonnel(member.id, castAssignments));

    return characters
        .filter(character => characterIds.has(character.id) && !character.deleted)
        .map(character => character.name.trim())
        .filter(Boolean);
}

export function getPersonnelProjectTitle(
    member: Personnel,
    options: PersonnelProjectTitleOptions = {},
): string {
    return firstTrimmedValue(
        getAssignedCharacterNames(member, options.characters, options.castAssignments).join(', '),
        member.title,
        member.primaryRole,
        member.department,
        member.category,
    );
}

/**
 * Utility to generate a readable character name string from CastAssignment records.
 * Truncates to "Character1 +N" pattern if more than 2 characters are assigned.
 */
export function getCharacterDisplayString(
    member: Personnel,
    characters: Character[],
    castAssignments: CastAssignment[]
): string {
    const characterIds = getCharacterIdsForPersonnel(member.id, castAssignments);

    if (characterIds.length === 0) {
        return '';
    }

    const assignedCharacters = characters.filter(c => 
        characterIds.includes(c.id) && !c.deleted
    );

    if (assignedCharacters.length === 0) {
        return '';
    }

    if (assignedCharacters.length === 1) {
        return assignedCharacters[0]!.name;
    }

    if (assignedCharacters.length === 2) {
        return `${assignedCharacters[0]!.name}, ${assignedCharacters[1]!.name}`;
    }

    // More than 2: show first character + count
    const remaining = assignedCharacters.length - 1;
    return `${assignedCharacters[0]!.name} +${remaining}`;
}

/**
 * Get a role/title display string for non-cast personnel
 */
export function getRoleDisplayString(member: Personnel): string {
    if (member.title) {
        return member.title;
    }
    
    if (member.primaryRole) {
        // Convert snake-case to Title Case
        return member.primaryRole
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    if (member.category === 'cast') {
        return 'Cast Member';
    }

    return member.category === 'crew' ? 'Crew Member' : 'Production Staff';
}
